import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import axiosInstance from '../services/AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { setAccessToken, setUserData, setPreSelectedCompany, logout } from '../store/actions/AuthActions';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();

    const initializeAuth = async () => {
        try {
            setIsLoading(true);

            // Attempt to refresh access token
            const refreshTokenUrlPath = getApiUrl('auth/refresh');
            const response = await axiosInstance.post(refreshTokenUrlPath, {}, { withCredentials: true });
            const { accessToken } = response.data;

            // Store access token in state
            dispatch(setAccessToken(accessToken));

            // Fetch user data
            const userApiUrlPath = getApiUrl('auth/users');
            const userResponse = await axiosInstance.get(userApiUrlPath);
            const userData = userResponse.data;

            const userId = userData.user.google_id;

            // Fetch user's associated companies
            const companiesApiUrlPath = getApiUrl(`auth/users/companies?googleId=${userId}`);
            const companiesResponse = await axiosInstance.get(companiesApiUrlPath, { withCredentials: true });
            const companiesData = companiesResponse.data;

            // Convert companies array to object for easy access
            const companiesObject = {};
            // Handle both wrapped response { companies: [...] } and direct array [...]
            const companiesArray = companiesData.companies || companiesData;
            if (companiesArray && Array.isArray(companiesArray)) {
                companiesArray.forEach(company => {
                    companiesObject[company.company_id] = {
                        companyName: company.company_name,
                        companyRegNo: company.company_reg_no || '',
                        roles: company.roles || []
                    };
                });
            }

            // Dispatch user data with companies
            dispatch(setUserData({
                ...userData,
                companies: companiesObject
            }));

            // Check for previously selected company in localStorage
            const storedCompanyString = localStorage.getItem('selectedCompany');

            if (storedCompanyString) {
                try {
                    const storedCompany = JSON.parse(storedCompanyString);
                    console.log('Found stored company:', storedCompany);

                    // Verify the stored company still exists in user's companies  
                    const companyExists = Object.keys(companiesObject).includes(storedCompany.companyId);

                    if (companyExists) {
                        console.log('Restoring previously selected company:', storedCompany.companyName);
                        dispatch(setPreSelectedCompany(storedCompany.companyId, storedCompany.companyName));
                    } else {
                        console.log('Stored company no longer accessible, clearing localStorage');
                        localStorage.removeItem('selectedCompany');
                    }
                } catch (parseError) {
                    console.error('Error parsing stored company data:', parseError);
                    localStorage.removeItem('selectedCompany');
                }
            }

        } catch (error) {
            console.error('Authentication initialization failed:', error);
            // Clear any invalid stored data
            localStorage.removeItem('selectedCompany');
            dispatch(logout());
        } finally {
            setIsLoading(false);
            setIsInitialized(true);
        }
    };

    useEffect(() => {
        initializeAuth();
    }, []);

    const value = {
        isInitialized,
        isLoading,
        refresh: initializeAuth
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};


// TODO: Add use the following in the future modre checks

// import React, { createContext, useContext, useEffect, useState } from 'react';
// import { useDispatch } from 'react-redux';
// import axiosInstance from '../services/AxiosInstance';
// import { getApiUrl } from '../utils/ApiUtils';
// import { setAccessToken, setUserData, setPreSelectedCompany, logout } from '../store/actions/AuthActions';

// const AuthContext = createContext();

// export const useAuth = () => {
//     const context = useContext(AuthContext);
//     if (!context) {
//         throw new Error('useAuth must be used within an AuthProvider');
//     }
//     return context;
// };

// export const AuthProvider = ({ children }) => {
//     const [isInitialized, setIsInitialized] = useState(false);
//     const [isLoading, setIsLoading] = useState(true);
//     const dispatch = useDispatch();

//     const initializeAuth = async () => {
//         try {
//             setIsLoading(true);

//             // Attempt to refresh access token
//             const refreshTokenUrlPath = getApiUrl('auth/refresh');
//             const response = await axiosInstance.post(refreshTokenUrlPath, {}, { withCredentials: true });
//             const { accessToken } = response.data;

//             // Store access token in state
//             dispatch(setAccessToken(accessToken));

//             // Fetch user data
//             const userApiUrlPath = getApiUrl('auth/users');
//             const userResponse = await axiosInstance.get(userApiUrlPath);
//             const userData = userResponse.data;
//             dispatch(setUserData(userData));

//             // Fetch user's associated companies
//             const companiesApiUrlPath = getApiUrl('auth/users/companies');
//             const companiesResponse = await axiosInstance.get(companiesApiUrlPath, { withCredentials: true });
//             const userCompanies = companiesResponse.data;

//             // Validate and restore company selection
//             await validateAndRestoreCompanySelection(userCompanies);

//         } catch (error) {
//             console.error('Authentication initialization failed:', error);
//             // Clear potentially invalid data
//             clearStoredCompanyData();
//             dispatch(logout());
//         } finally {
//             setIsLoading(false);
//             setIsInitialized(true);
//         }
//     };

//     const validateAndRestoreCompanySelection = async (userCompanies) => {
//         const storedCompanyString = localStorage.getItem('selectedCompany');

//         if (!storedCompanyString) {
//             console.log('No stored company found');
//             return;
//         }

//         try {
//             const storedCompany = JSON.parse(storedCompanyString);

//             // Check if stored data has required fields
//             if (!storedCompany.companyId || !storedCompany.companyName) {
//                 console.log('Invalid stored company data structure');
//                 clearStoredCompanyData();
//                 return;
//             }

//             // Check if stored company still exists in user's accessible companies
//             const companyExists = userCompanies.some(company =>
//                 String(company.id) === String(storedCompany.companyId) ||
//                 String(company.companyId) === String(storedCompany.companyId)
//             );

//             if (!companyExists) {
//                 console.log('Stored company no longer accessible to user');
//                 clearStoredCompanyData();
//                 return;
//             }

//             // Additional server-side validation (optional but recommended)
//             try {
//                 const validationUrl = getApiUrl(`${storedCompany.companyId}/validate-access`);
//                 await axiosInstance.get(validationUrl, { withCredentials: true });

//                 console.log('Company access validated, restoring selection:', storedCompany.companyName);
//                 dispatch(setPreSelectedCompany(storedCompany.companyId, storedCompany.companyName));

//                 // Update timestamp
//                 updateStoredCompanyTimestamp(storedCompany);

//             } catch (validationError) {
//                 console.log('Server-side validation failed for stored company');
//                 clearStoredCompanyData();
//             }

//         } catch (parseError) {
//             console.error('Error parsing stored company data:', parseError);
//             clearStoredCompanyData();
//         }
//     };

//     const clearStoredCompanyData = () => {
//         localStorage.removeItem('selectedCompany');
//         console.log('Cleared invalid stored company data');
//     };

//     const updateStoredCompanyTimestamp = (companyData) => {
//         const updatedData = {
//             ...companyData,
//             lastAccessed: new Date().toISOString(),
//             expiresAt: new Date(Date.now() + (7 * 24 * 60 * 60 * 1000)).toISOString() // 7 days
//         };
//         localStorage.setItem('selectedCompany', JSON.stringify(updatedData));
//     };

//     useEffect(() => {
//         initializeAuth();
//     }, []);

//     const value = {
//         isInitialized,
//         isLoading,
//         refresh: initializeAuth
//     };

//     return (
//         <AuthContext.Provider value={value}>
//             {children}
//         </AuthContext.Provider>
//     );
// };